import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { LensScopePicker } from "./LensScopePicker";

// Add / remove requirements (from any standard) on an existing ClaroLens review.
export const LensScopeDialog = ({ open, onOpenChange, review, onSaved }) => {
  const [stdIds, setStdIds] = useState([]);
  const [selection, setSelection] = useState({});
  const [focus, setFocus] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !review) return;
    const parts = review.part_selections || {};
    const inScope = (review.requirements || []).filter((r) => review.cross_standard || r.applicable);
    setSelection(Object.fromEntries(inScope.map((r) => [r.id, parts[r.id] || []])));
    setStdIds(review.standard_ids || [review.standard_id]);
    setFocus(review.focus || null);
  }, [open, review]);

  const save = async () => {
    const ids = Object.keys(selection);
    if (!ids.length) return toast.error("Keep at least one requirement");
    setSaving(true);
    try {
      const { data } = await api.put(`/rapid-reviews/${review.id}/scope`, {
        requirement_ids: ids, part_selections: selection, focus_term: focus?.term || "", focus_functions: focus?.functions || [], focus_instructions: focus?.instructions || "",
      });
      toast.success("Review requirements updated");
      onSaved(data);
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="rr-scope-dialog" className="rounded-none max-w-2xl max-h-[88vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Add or remove requirements</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground -mt-2">
          Pull in requirements from any standard. Requirements that already have questions can't be removed until those questions are deleted.
        </p>
        {open && <LensScopePicker stdIds={stdIds} setStdIds={setStdIds} selection={selection} setSelection={setSelection}
          focus={focus} setFocus={setFocus} />}
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="rr-scope-save" className="rounded-none" disabled={saving} onClick={save}>
            {saving ? "Saving…" : "Save requirements"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
